import { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import 'moment/locale/fr';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { User } from 'services/auth/auth.types';
import { useGetUserProfileMutation } from 'services/user/user.endpoint';
import {
    useCreateAppointmentMutation,
    useGetAllAppointmentsMutation
} from 'services/appointment/appointment.endpoint';
import { getDatalists } from 'features/datalist';

moment.locale('fr');

export type Step = 'list' | 'day' | 'calendar' | 'time' | 'summary';
export type DayChoice = 'today' | 'tomorrow' | 'other';
export type TimeChoice = 'morning' | 'afternoon';
export type RequestType = 'appointment' | 'exit';

export default function useNewAppointmentVM() {
    const [me, setMe] = useState<User | null>(null);
    const [appointments, setAppointments] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const [step, setStep] = useState<Step>('list');
    const [requestType, setRequestType] = useState<RequestType>('appointment');
    const [exitReason, setExitReason] = useState('');
    const [dayChoice, setDayChoice] = useState<DayChoice>('today');
    const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
    const [timeChoice, setTimeChoice] = useState<TimeChoice>('morning');
    const [weekOffset, setWeekOffset] = useState(0);

    const [getMe] = useGetUserProfileMutation();
    const [getAppointments] = useGetAllAppointmentsMutation();
    const [createAppointment] = useCreateAppointmentMutation();
    const { datalists } = useSelector(getDatalists);
    const location = useLocation();

    useEffect(() => {
        (async () => {
            const meRes = await getMe(undefined).unwrap();
            setMe(meRes.data);
            const projectId = (meRes.data as any)?.projects?.[0]?.id;
            if (projectId) {
                const apptRes = await getAppointments({ project_id: String(projectId) } as any).unwrap();
                setAppointments(apptRes.data ?? []);
            }
            setIsLoading(false);
        })();
    }, [getMe, getAppointments]);

    const upcoming = useMemo(() => {
        const now = moment().startOf('day');
        return appointments
            .filter((a) => a.status !== 'refused' && moment(a.started_at).isSameOrAfter(now, 'day'))
            .sort((a, b) => moment(a.started_at).valueOf() - moment(b.started_at).valueOf());
    }, [appointments]);

    const resolvedDate = useMemo<Moment | null>(() => {
        if (dayChoice === 'today') return moment().startOf('day');
        if (dayChoice === 'tomorrow') return moment().add(1, 'day').startOf('day');
        return selectedDate;
    }, [dayChoice, selectedDate]);

    const resetFlowState = () => {
        setDayChoice('today');
        setSelectedDate(null);
        setTimeChoice('morning');
        setWeekOffset(0);
        setExitReason('');
    };

    const startFlow = (type: RequestType = 'appointment') => {
        resetFlowState();
        setRequestType(type);
        setStep('day');
    };

    const cancelFlow = () => {
        resetFlowState();
        setRequestType('appointment');
        setStep('list');
    };

    // Démarre directement le flux "sortie du dispositif" si on arrive depuis le bouton dédié de "Mon dossier"
    useEffect(() => {
        if ((location.state as any)?.startExit) {
            startFlow('exit');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goNext = () => {
        if (step === 'day') setStep(dayChoice === 'other' ? 'calendar' : 'time');
        else if (step === 'calendar') setStep('time');
        else if (step === 'time') setStep('summary');
    };

    const goBack = () => {
        if (step === 'day') setStep('list');
        else if (step === 'calendar') setStep('day');
        else if (step === 'time') setStep(dayChoice === 'other' ? 'calendar' : 'day');
        else if (step === 'summary') setStep('time');
    };

    const stepNumber = useMemo(() => {
        if (step === 'list') return 0;
        if (step === 'day') return 1;
        if (dayChoice === 'other') {
            if (step === 'calendar') return 2;
            if (step === 'time') return 3;
            if (step === 'summary') return 4;
        } else {
            if (step === 'time') return 2;
            if (step === 'summary') return 3;
        }
        return 0;
    }, [step, dayChoice]);

    const totalSteps = dayChoice === 'other' ? 4 : 3;

    const submit = async () => {
        if (!resolvedDate) return;
        const projectId = (me as any)?.projects?.[0]?.id;
        if (!projectId) return;

        const isMorning = timeChoice === 'morning';
        const startTime = isMorning ? '09:00' : '14:00';
        const endTime = isMorning ? '12:00' : '17:00';
        const dateStr = resolvedDate.format('YYYY-MM-DD');
        const tz = '+01:00';

        const communicationType = Object.keys(datalists?.communication_types ?? {})[0] ?? '';
        const isExit = requestType === 'exit';

        await createAppointment({
            project_id: String(projectId),
            type: requestType,
            label: isExit ? 'Demande de sortie du dispositif' : 'Demande de rendez-vous',
            observation: `Préférence : ${isMorning ? 'matin' : 'après-midi'}`,
            reason: isExit ? exitReason : undefined,
            started_at: `${dateStr}T${startTime}:00${tz}`,
            ended_at: `${dateStr}T${endTime}:00${tz}`,
            communication_type: communicationType
        }).unwrap();

        const apptRes = await getAppointments({ project_id: String(projectId) } as any).unwrap();
        setAppointments(apptRes.data ?? []);
        cancelFlow();
    };

    return {
        isLoading,
        upcoming,
        step,
        stepNumber,
        totalSteps,
        requestType,
        exitReason,
        setExitReason,
        dayChoice,
        setDayChoice,
        selectedDate,
        setSelectedDate,
        timeChoice,
        setTimeChoice,
        weekOffset,
        setWeekOffset,
        resolvedDate,
        startFlow,
        cancelFlow,
        goNext,
        goBack,
        submit
    };
}
